import { categoriesService, ordersService, registerSessionsService, reportsDailySummaryService, rosterService, timesheetService, couponCampaignsService, couponsService, couponUsageLogsService } from './database';
import { resolveBuildingWing } from './buildingUnitUtils';
import { paidHoursRounded } from './nzRosterBreaks';
import { getOrderTotalFromItems, getOrderTotals } from './orderUtils';
import type { Category, Order, OrderItem, RegisterSession, Table, MenuItem, Recipe, InventoryItem, KitchenSLASettings } from '../types';

export interface DailySalesRow {
  date: string;
  totalOrders: number;
  dineInOrders: number;
  takeawayOrders: number;
  deliveryOrders: number;
  grossSales: number;
  discounts: number;
  netSales: number;
  taxCollected: number;
  cashPayments: number;
  cardPayments: number;
  /** Bank transfer / Account2Account (manual POS). */
  bankTransferPayments: number;
}

export interface MonthlySalesRow {
  date: string;
  totalOrders: number;
  dineInOrders: number;
  dineInAmount: number;
  takeawayOrders: number;
  takeawayAmount: number;
  deliveryOrders: number;
  deliveryAmount: number;
  uberDoordashOrders: number;
  uberDoordashAmount: number;
  totalSales: number;
  cashSales: number;
  cardSales: number;
  bankTransferSales: number;
  discount: number;
  gstCollected: number;
  cardTerminalIds: string;
}

export interface RegisterLogRow {
  id: string;
  date: string;
  terminalName?: string;
  terminalId: string;
  cashierName: string;
  cashierEmail?: string;
  openingFloat: number;
  closingFloat?: number;
  cashSales: number;
  cardSales: number;
  variance?: number;
  startTime: number;
  endTime?: number;
  durationHours?: number;
  isActive: boolean;
  lastReminderAt?: number;
}

export interface PaymentBreakdownRow {
  method: string;
  amount: number;
  orderCount: number;
  percentage: number;
}

export interface CategoryPerformanceRow {
  categoryId: number;
  categoryName: string;
  itemsSold: number;
  grossSales: number;
  share: number;
  averageItemValue: number;
}

export interface TopItemRow {
  itemId: number;
  name: string;
  categoryName: string;
  quantity: number;
  grossSales: number;
}

// Detailed per-order, per-item sales row for CSV export
export interface ItemSalesDetailRow {
  date: string;
  dateTime: string;
  orderId: string;
  orderType: string;
  itemId: number;
  itemName: string;
  categoryName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface DiscountSummary {
  totalDiscount: number;
  manualDiscount: number;
  rewardDiscount: number;
  couponDiscount: number;
  residentDiscount: number;
  ordersWithDiscount: number;
  approvalsRequired: number;
  approvalsCompleted: number;
  approvalsPending: number;
}

export interface SalesInsightSummary {
  totalOrders: number;
  grossSales: number;
  netSales: number;
  itemsSold: number;
  averageOrderValue: number;
  averageItemsPerOrder: number;
}

export interface SalesInsights {
  payments: PaymentBreakdownRow[];
  categoryPerformance: CategoryPerformanceRow[];
  /** Top 15 items by gross sales (for on-screen table). */
  topItems: TopItemRow[];
  /** Every menu item sold in the range (for CSV export). */
  allItems: TopItemRow[];
  discountSummary: DiscountSummary;
  summary: SalesInsightSummary;
}

export interface CouponCampaignDashboardRow {
  campaignId: string;
  campaignName: string;
  campaignKind?: 'promotional' | 'building_unit';
  totalCoupons: number;
  used: number;
  remaining: number;
  expired: number;
}

export interface BuildingUnitPerUnitRow {
  unitLabel: string;
  buildingWing?: 'hotel_wing' | 'tower_wing';
  couponCode?: string;
  redemptionCount: number;
  totalDiscount: number;
}

export interface BuildingUnitDashboardStats {
  hotelWingIssued: number;
  hotelWingEverUsed: number;
  towerWingIssued: number;
  towerWingEverUsed: number;
  neverUsed: number;
  totalDiscountInRange: number;
  perUnit: BuildingUnitPerUnitRow[];
}

export interface CouponDashboardAnalytics {
  campaigns: CouponCampaignDashboardRow[];
  totalRevenueFromCoupons: number;
  usageByStaff: { staffId: number; redemptions: number; discountAmount: number }[];
  recentRedemptions: {
    couponCode: string;
    campaignId?: string;
    orderId?: string;
    staffId?: number;
    usedAt: number;
    unitLabel?: string;
  }[];
  buildingUnits?: BuildingUnitDashboardStats;
}

/** One row per paid order (invoice) for reconciliation: what was recorded as cash vs card per order */
export interface SalesByInvoiceRow {
  orderId: string;
  date: string;
  dateTime: string;
  timestamp: number;
  orderType: string;
  totalAmount: number;
  cashAmount: number;
  cardAmount: number;
  otherAmount: number;
  sessionId?: string;
  terminalId?: string;
}

export interface SalesByInvoicePage {
  rows: SalesByInvoiceRow[];
  nextCursor: { timestamp: number; id: string } | null;
}

interface DateRange {
  start: number;
  end: number;
}

/**
 * Get start/end timestamps for a date range in **local** timezone (midnight to end of day).
 * Must match formatDate (local) so orders are grouped on the correct day.
 */
function getDateRange(startDate: string, endDate: string): DateRange {
  const now = new Date();
  if (!startDate || !endDate) {
    return { start: now.getTime(), end: now.getTime() };
  }
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(sy, (sm || 1) - 1, sd || 1, 0, 0, 0, 0);
  const end = new Date(ey, (em || 1) - 1, ed || 1, 23, 59, 59, 999);
  return { start: start.getTime(), end: end.getTime() };
}

/**
 * Format a timestamp as YYYY-MM-DD in **local** timezone.
 * Used so daily grouping matches the date range (which uses local boundaries).
 * Using toISOString() would group by UTC date and cause wrong totals across timezones.
 */
function formatDate(timestamp: number): string {
  const d = new Date(timestamp);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Gross = sum of line totals for items actually sold (exclude voided/removed lines), plus manual delivery charge. Matches what invoices total before discounts. */
function calculateOrderGross(order: Order) {
  const itemsGross = (order.items ?? []).reduce(
    (sum, item) => sum + (item.removed ? 0 : item.price * item.quantity),
    0
  );
  const delivery =
    order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
  return itemsGross + delivery;
}

function buildCategoryMap(categories: Category[]) {
  const map = new Map<string, string>();
  categories.forEach((category) => {
    map.set(String(category.id), category.name || `Category ${category.id}`);
  });
  return map;
}

function getOrderItemQuantity(order: Order) {
  return order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
}

export async function getRegisterLogs(startDate: string, endDate: string, includeActiveSessions?: RegisterSession[]): Promise<RegisterLogRow[]> {
  const { start, end } = getDateRange(startDate, endDate);
  const sessions = await registerSessionsService.getByDateRange(start, end);
  
  // Include active sessions that might be outside the date range
  const allSessions = [...sessions];
  if (includeActiveSessions) {
    includeActiveSessions.forEach(activeSession => {
      // Only add if not already in the list
      if (!allSessions.find(s => s.id === activeSession.id)) {
        allSessions.push(activeSession);
      }
    });
  }
  
  return allSessions.map((session: RegisterSession) => {
    const duration = session.endTime ? (session.endTime - session.startTime) / (1000 * 60 * 60) : undefined;
    return {
      id: session.id,
      date: formatDate(session.startTime),
      terminalName: session.terminalName,
      terminalId: session.terminalId,
      cashierName: session.cashierName,
      cashierEmail: session.cashierEmail,
      openingFloat: session.openingFloat,
      closingFloat: session.closingFloat,
      cashSales: session.cashSales,
      cardSales: session.cardSales,
      variance: session.variance,
      startTime: session.startTime,
      endTime: session.endTime,
      durationHours: duration ? Number(duration.toFixed(2)) : undefined,
      isActive: session.isActive,
      lastReminderAt: session.lastReminderAt,
    };
  });
}

export async function getDailySales(startDate: string, endDate: string): Promise<DailySalesRow[]> {
  try {
    const summaryRows = await reportsDailySummaryService.getByDateRange(startDate, endDate);
    if (summaryRows.length > 0) {
      return summaryRows.map((row) => ({
        date: row.date,
        totalOrders: row.totalOrders ?? 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        grossSales: row.totalRevenue ?? 0,
        discounts: row.discountTotal ?? 0,
        netSales: Math.max(0, (row.totalRevenue ?? 0) - (row.gstCollected ?? 0)),
        taxCollected: row.gstCollected ?? 0,
        cashPayments: 0,
        cardPayments: 0,
        bankTransferPayments: 0,
      }));
    }
  } catch (error) {
    // If summary collections are not readable in this environment, keep reports functional
    // by falling back to raw-order aggregation logic below.
    console.warn('reports_daily unavailable; falling back to raw daily sales query.', error);
  }
  const { start, end } = getDateRange(startDate, endDate);
  const orders = await ordersService.getByDateRange(start, end);
  // Exclude cancelled and refunded orders from daily sales
  // Treat orders as "paid" if either paymentStatus is 'paid' OR there are payment records with amount > 0.
  const paidOrders = orders.filter((order) => {
    const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
    const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
    return (
      isPaidLike &&
      order.status !== 'cancelled' &&
      order.status !== 'refunded'
    );
  });

  const totals = new Map<string, DailySalesRow>();

  paidOrders.forEach((order) => {
    const dateKey = formatDate(order.timestamp);
    if (!totals.has(dateKey)) {
      totals.set(dateKey, {
        date: dateKey,
        totalOrders: 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        grossSales: 0,
        discounts: 0,
        netSales: 0,
        taxCollected: 0,
        cashPayments: 0,
        cardPayments: 0,
        bankTransferPayments: 0,
      });
    }

    const entry = totals.get(dateKey)!;
    entry.totalOrders += 1;
    if (order.orderType === 'dine-in') entry.dineInOrders += 1;
    if (order.orderType === 'takeaway') entry.takeawayOrders += 1;
    if (order.orderType === 'delivery') entry.deliveryOrders += 1;

    const gross = calculateOrderGross(order);
    const { subtotal, taxAmount } = getOrderTotals(order);
    const totalAmount = getOrderTotalFromItems(order) ?? order.totalAmount ?? gross;
    entry.grossSales += gross;
    const discount = order.discountAmount ?? Math.max(0, gross - totalAmount);
    entry.discounts += discount;
    // Net Sales = amount after tax (GST excluded), i.e. subtotal ex-GST
    entry.netSales += subtotal;
    entry.taxCollected += taxAmount;

    order.payments.forEach((payment) => {
      if (payment.method === 'cash') {
        entry.cashPayments += payment.amount;
      } else if (payment.method === 'card' || payment.method === 'card_online') {
        entry.cardPayments += payment.amount;
      } else if (payment.method === 'account2account') {
        entry.bankTransferPayments += payment.amount;
      }
    });
  });

  return Array.from(totals.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Daily rows for a selected month (YYYY-MM), including order-type and payment splits.
 */
export async function getMonthlySalesBreakdown(month: string): Promise<MonthlySalesRow[]> {
  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return [];
  }

  const monthStart = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  const orders = await ordersService.getByDateRange(monthStart.getTime(), monthEnd.getTime());
  const paidOrders = orders.filter((order) => {
    const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
    const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
    return isPaidLike && order.status !== 'cancelled' && order.status !== 'refunded';
  });

  const byDay = new Map<string, MonthlySalesRow & { terminalSet: Set<string> }>();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const date = formatDate(new Date(year, monthIndex, day, 12, 0, 0, 0).getTime());
    byDay.set(date, {
      date,
      totalOrders: 0,
      dineInOrders: 0,
      dineInAmount: 0,
      takeawayOrders: 0,
      takeawayAmount: 0,
      deliveryOrders: 0,
      deliveryAmount: 0,
      uberDoordashOrders: 0,
      uberDoordashAmount: 0,
      totalSales: 0,
      cashSales: 0,
      cardSales: 0,
      bankTransferSales: 0,
      discount: 0,
      gstCollected: 0,
      cardTerminalIds: '',
      terminalSet: new Set<string>(),
    });
  }

  paidOrders.forEach((order) => {
    const dateKey = formatDate(order.timestamp);
    const row = byDay.get(dateKey);
    if (!row) return;

    const amount = getOrderTotalFromItems(order) ?? order.totalAmount ?? 0;
    const gross = calculateOrderGross(order);
    const discount = order.discountAmount ?? Math.max(0, gross - amount);
    const taxAmount = getOrderTotals(order).taxAmount;

    row.totalOrders += 1;
    row.totalSales += amount;
    row.discount += discount;
    row.gstCollected += taxAmount;

    if (order.orderType === 'dine-in') {
      row.dineInOrders += 1;
      row.dineInAmount += amount;
    } else if (order.orderType === 'takeaway') {
      row.takeawayOrders += 1;
      row.takeawayAmount += amount;
    } else if (order.orderType === 'delivery') {
      row.deliveryOrders += 1;
      row.deliveryAmount += amount;
    } else if (order.orderType === 'uber-doordash') {
      row.uberDoordashOrders += 1;
      row.uberDoordashAmount += amount;
    }

    (order.payments || []).forEach((payment) => {
      if (payment.method === 'cash') {
        row.cashSales += payment.amount;
      } else if (payment.method === 'card' || payment.method === 'card_online') {
        row.cardSales += payment.amount;
        if (payment.terminalId) row.terminalSet.add(payment.terminalId);
      } else if (payment.method === 'account2account') {
        row.bankTransferSales += payment.amount;
      }
    });
  });

  return Array.from(byDay.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((row) => ({
      ...row,
      cardTerminalIds: Array.from(row.terminalSet.values()).sort().join(', '),
    }));
}

function eachDateStringInclusiveYmd(start: string, end: string): string[] {
  if (!start || !end || start > end) return [];
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  if ([ys, ms, ds, ye, me, de].some((n) => !Number.isFinite(n))) return [];
  const t0 = new Date(ys, ms - 1, ds, 12, 0, 0, 0).getTime();
  const t1 = new Date(ye, me - 1, de, 12, 0, 0, 0).getTime();
  if (t0 > t1) return [];
  const out: string[] = [];
  for (let t = t0; t <= t1; t += 24 * 60 * 60 * 1000) {
    const d = new Date(t);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

/** Distinct YYYY-MM values covered by an inclusive date span (sorted). */
export function monthsSpanningDateRange(start: string, end: string): string[] {
  const set = new Set<string>();
  for (const d of eachDateStringInclusiveYmd(start, end)) {
    set.add(d.slice(0, 7));
  }
  return Array.from(set).sort();
}

function emptyMonthlySalesRow(date: string): MonthlySalesRow {
  return {
    date,
    totalOrders: 0,
    dineInOrders: 0,
    dineInAmount: 0,
    takeawayOrders: 0,
    takeawayAmount: 0,
    deliveryOrders: 0,
    deliveryAmount: 0,
    uberDoordashOrders: 0,
    uberDoordashAmount: 0,
    totalSales: 0,
    cashSales: 0,
    cardSales: 0,
    bankTransferSales: 0,
    discount: 0,
    gstCollected: 0,
    cardTerminalIds: '',
  };
}

/**
 * Same rules as {@link getMonthlySalesBreakdown}, but for an arbitrary inclusive calendar span.
 * Loads each touched calendar month and merges rows by date (missing days are zero-filled).
 */
export async function getMonthlySalesBreakdownForDateRange(start: string, end: string): Promise<MonthlySalesRow[]> {
  if (!start || !end || start > end) return [];
  const months = monthsSpanningDateRange(start, end);
  const byDate = new Map<string, MonthlySalesRow>();
  for (const ym of months) {
    const rows = await getMonthlySalesBreakdown(ym);
    for (const row of rows) {
      if (row.date >= start && row.date <= end) {
        byDate.set(row.date, row);
      }
    }
  }
  return eachDateStringInclusiveYmd(start, end).map((date) => byDate.get(date) ?? emptyMonthlySalesRow(date));
}

/**
 * Sales by invoice: one row per paid order with cash/card/other breakdown.
 * Use for reconciliation when register closing used actual cash but orders were rung as cash/card.
 */
export async function getSalesByInvoice(startDate: string, endDate: string): Promise<SalesByInvoiceRow[]> {
  const allRows: SalesByInvoiceRow[] = [];
  let cursor: { timestamp: number; id: string } | null = null;
  do {
    const page = await getSalesByInvoicePage(startDate, endDate, {
      pageSize: 50,
      cursorTimestamp: cursor?.timestamp,
      cursorId: cursor?.id,
    });
    allRows.push(...page.rows);
    cursor = page.nextCursor;
  } while (cursor);
  return allRows;
}

export async function getSalesByInvoicePage(
  startDate: string,
  endDate: string,
  options?: { pageSize?: number; cursorTimestamp?: number; cursorId?: string },
): Promise<SalesByInvoicePage> {
  const { start, end } = getDateRange(startDate, endDate);
  const page = await ordersService.getByDateRangePage(start, end, options);
  const orders = page.rows;
  // Treat orders as "paid" if either paymentStatus is 'paid' OR there are payment records with amount > 0.
  const paidOrders = orders.filter((order) => {
    const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
    const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
    return (
      isPaidLike &&
      order.status !== 'cancelled' &&
      order.status !== 'refunded' &&
      order.status !== 'comped' &&
      !order.isComped
    );
  });

  const rows = paidOrders
    .map((order) => {
      let cashAmount = 0;
      let cardAmount = 0;
      let otherAmount = 0;
      (order.payments || []).forEach((p) => {
        const m = (p.method || '').toString().toLowerCase();
        if (m === 'cash') cashAmount += p.amount;
        else if (m === 'card' || m === 'card_online') cardAmount += p.amount;
        else otherAmount += p.amount;
      });
      const totalAmount = getOrderTotalFromItems(order);
      const d = new Date(order.timestamp);
      const dateStr = formatDate(order.timestamp);
      const timeStr = d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      return {
        orderId: order.id,
        date: dateStr,
        dateTime: `${dateStr} ${timeStr}`,
        timestamp: order.timestamp,
        orderType: order.orderType || '—',
        totalAmount,
        cashAmount,
        cardAmount,
        otherAmount,
        sessionId: order.sessionId,
        terminalId: (order.payments && order.payments[0]?.terminalId) || undefined,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
  return { rows, nextCursor: page.nextCursor };
}

/**
 * Detailed per-order, per-item sales for selected menu items over a date range.
 * Can be filtered to one or more itemIds; when itemIds is empty, returns all items.
 */
export async function getItemSalesDetail(
  startDate: string,
  endDate: string,
  itemIds: number[] | null | undefined,
): Promise<ItemSalesDetailRow[]> {
  const { start, end } = getDateRange(startDate, endDate);
  const [orders, categories] = await Promise.all([
    ordersService.getByDateRange(start, end),
    categoriesService.getAll(),
  ]);

  const categoryMap = buildCategoryMap(categories);
  const allowedIds = (itemIds ?? []).filter((id) => Number.isFinite(id));
  const filterByItem = allowedIds.length > 0;

  const rows: ItemSalesDetailRow[] = [];

  orders.forEach((order) => {
    // Treat as "paid" like other reports
    const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
    const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
    if (!isPaidLike || order.status === 'cancelled' || order.status === 'refunded') {
      return;
    }

    const d = new Date(order.timestamp);
    const dateStr = formatDate(order.timestamp);
    const timeStr = d.toLocaleTimeString('en-NZ', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    (order.items || []).forEach((item: OrderItem) => {
      if (item.removed) return;
      if (filterByItem && !allowedIds.includes(item.id)) return;

      const qty = item.quantity ?? 0;
      if (!qty) return;

      const unitPrice = item.price ?? 0;
      const lineTotal = unitPrice * qty;
      const catKey =
        item.categoryId != null && item.categoryId !== '' ? String(item.categoryId) : 'uncategorised';
      const categoryName = categoryMap.get(catKey) || 'Uncategorised';

      rows.push({
        date: dateStr,
        dateTime: `${dateStr} ${timeStr}`,
        orderId: order.id,
        orderType: order.orderType || '—',
        itemId: item.id,
        itemName: item.name,
        categoryName,
        quantity: qty,
        unitPrice,
        lineTotal,
      });
    });
  });

  return rows.sort((a, b) => {
    if (a.dateTime === b.dateTime) {
      if (a.orderId === b.orderId) return a.itemName.localeCompare(b.itemName);
      return a.orderId.localeCompare(b.orderId);
    }
    return a.dateTime.localeCompare(b.dateTime);
  });
}

export async function getSalesInsights(startDate: string, endDate: string): Promise<SalesInsights> {
  const { start, end } = getDateRange(startDate, endDate);
  const [orders, categories] = await Promise.all([
    ordersService.getByDateRange(start, end),
    categoriesService.getAll(),
  ]);
  const paidOrders = orders.filter(
    (order) =>
      order.paymentStatus === 'paid' &&
      order.status !== 'cancelled' &&
      order.status !== 'refunded'
  );
  const categoryMap = buildCategoryMap(categories);

  const paymentTotals = new Map<
    string,
    { amount: number; orderIds: Set<string> }
  >();
  const categoryTotals = new Map<
    string,
    { name: string; itemsSold: number; grossSales: number }
  >();
  const itemTotals = new Map<
    number,
    { name: string; categoryName: string; quantity: number; grossSales: number }
  >();
  const discountSummary: DiscountSummary = {
    totalDiscount: 0,
    manualDiscount: 0,
    rewardDiscount: 0,
    couponDiscount: 0,
    residentDiscount: 0,
    ordersWithDiscount: 0,
    approvalsRequired: 0,
    approvalsCompleted: 0,
    approvalsPending: 0,
  };
  let grossSales = 0;
  let netSales = 0;
  let totalItems = 0;

  paidOrders.forEach((order) => {
    const orderGross = calculateOrderGross(order);
    const orderNet = order.totalAmount ?? orderGross;
    const itemsCount = getOrderItemQuantity(order);
    grossSales += orderGross;
    netSales += orderNet;
    totalItems += itemsCount;

    order.payments.forEach((payment) => {
      const method = payment.method || 'other';
      if (!paymentTotals.has(method)) {
        paymentTotals.set(method, { amount: 0, orderIds: new Set<string>() });
      }
      const entry = paymentTotals.get(method)!;
      entry.amount += payment.amount;
      entry.orderIds.add(order.id);
    });

    order.items.forEach((item: OrderItem) => {
      const catKey =
        item.categoryId != null && item.categoryId !== '' ? String(item.categoryId) : 'uncategorised';
      const catName = categoryMap.get(catKey) || 'Uncategorised';
      if (!categoryTotals.has(catKey)) {
        categoryTotals.set(catKey, {
          name: catName,
          itemsSold: 0,
          grossSales: 0,
        });
      }
      const categoryEntry = categoryTotals.get(catKey)!;
      categoryEntry.itemsSold += item.quantity;
      categoryEntry.grossSales += item.price * item.quantity;

      if (!itemTotals.has(item.id)) {
        itemTotals.set(item.id, {
          name: item.name,
          categoryName: catName,
          quantity: 0,
          grossSales: 0,
        });
      }
      const itemEntry = itemTotals.get(item.id)!;
      itemEntry.quantity += item.quantity;
      itemEntry.grossSales += item.price * item.quantity;
    });

    const appliedDiscount = order.discountAmount ?? 0;
    if (appliedDiscount > 0) {
      discountSummary.totalDiscount += appliedDiscount;
      discountSummary.ordersWithDiscount += 1;
    }
    if (order.manualDiscount) {
      discountSummary.manualDiscount += order.manualDiscount;
      if (order.discountRequiresApproval) {
        discountSummary.approvalsRequired += 1;
        if (order.discountApprovedBy) {
          discountSummary.approvalsCompleted += 1;
        } else {
          discountSummary.approvalsPending += 1;
        }
      }
    }
    if (order.appliedReward) {
      discountSummary.rewardDiscount += order.appliedReward.discountValue;
    }
    if (order.couponDiscountAmount) {
      discountSummary.couponDiscount += order.couponDiscountAmount;
    }
    if (order.residentDiscountAmount) {
      discountSummary.residentDiscount += order.residentDiscountAmount;
    }
  });

  const totalPaymentAmount = Array.from(paymentTotals.values()).reduce(
    (sum, entry) => sum + entry.amount,
    0
  );
  const payments: PaymentBreakdownRow[] = Array.from(paymentTotals.entries()).map(
    ([method, { amount, orderIds }]) => ({
      method:
        method === 'other'
          ? 'Other'
          : method === 'account2account'
            ? 'Bank transfer'
            : method === 'card_online'
              ? 'Card (online)'
              : method.charAt(0).toUpperCase() + method.slice(1),
      amount,
      orderCount: orderIds.size,
      percentage: totalPaymentAmount ? (amount / totalPaymentAmount) * 100 : 0,
    })
  );

  const categoryPerformance: CategoryPerformanceRow[] = Array.from(
    categoryTotals.entries()
  )
    .map(([catKey, data]) => ({
      categoryId: catKey === 'uncategorised' ? (0 as number) : Number.isFinite(Number(catKey)) ? Number(catKey) : 0,
      categoryName: data.name,
      itemsSold: data.itemsSold,
      grossSales: data.grossSales,
      share: grossSales ? (data.grossSales / grossSales) * 100 : 0,
      averageItemValue:
        data.itemsSold > 0 ? data.grossSales / data.itemsSold : 0,
    }))
    .sort((a, b) => b.grossSales - a.grossSales);

  const allItemsSorted: TopItemRow[] = Array.from(itemTotals.entries())
    .map(([itemId, data]) => ({
      itemId,
      name: data.name,
      categoryName: data.categoryName,
      quantity: data.quantity,
      grossSales: data.grossSales,
    }))
    .sort((a, b) => b.grossSales - a.grossSales);
  const topItems = allItemsSorted.slice(0, 15);
  const allItems = allItemsSorted;

  const totalOrders = paidOrders.length;
  const summary: SalesInsightSummary = {
    totalOrders,
    grossSales,
    netSales,
    itemsSold: totalItems,
    averageOrderValue: totalOrders > 0 ? netSales / totalOrders : 0,
    averageItemsPerOrder: totalOrders > 0 ? totalItems / totalOrders : 0,
  };

  return {
    payments,
    categoryPerformance,
    topItems,
    allItems,
    discountSummary,
    summary,
  };
}

export async function getCouponDashboardAnalytics(startDate: string, endDate: string): Promise<CouponDashboardAnalytics> {
  const { start, end } = getDateRange(startDate, endDate);
  const [campaigns, coupons, orders, usageLogs] = await Promise.all([
    couponCampaignsService.getAll(),
    couponsService.getAll(),
    ordersService.getByDateRange(start, end),
    couponUsageLogsService.getRecent(100),
  ]);

  const campaignsRows: CouponCampaignDashboardRow[] = campaigns.map((campaign) => {
    const campaignCoupons = coupons.filter((c) => c.campaignId === campaign.id);
    const used = campaignCoupons.filter((c) =>
      campaign.campaignKind === 'building_unit' || c.isPermanent
        ? (c.usedCount ?? 0) > 0
        : c.status === 'USED'
    ).length;
    const expired = campaignCoupons.filter((c) => c.status === 'EXPIRED').length;
    return {
      campaignId: campaign.id,
      campaignName: campaign.name,
      campaignKind: campaign.campaignKind ?? 'promotional',
      totalCoupons: campaignCoupons.length,
      used,
      remaining: Math.max(0, campaignCoupons.length - used - expired),
      expired,
    };
  });

  const paidOrders = orders.filter((o) => o.paymentStatus === 'paid' && o.status !== 'cancelled' && o.status !== 'refunded');
  const totalRevenueFromCoupons = paidOrders.reduce((sum, o) => sum + (o.couponDiscountAmount ?? 0), 0);

  const buildingCoupons = coupons.filter((c) => {
    const camp = campaigns.find((x) => x.id === c.campaignId);
    return c.isPermanent || camp?.campaignKind === 'building_unit';
  });
  const wingOf = (c: (typeof buildingCoupons)[number]) =>
    resolveBuildingWing({
      buildingWing: c.buildingWing,
      unitLabel: c.unitLabel,
      unitType: c.unitType,
    });
  const hotelWingCoupons = buildingCoupons.filter((c) => wingOf(c) === 'hotel_wing');
  const towerWingCoupons = buildingCoupons.filter((c) => wingOf(c) === 'tower_wing');
  const buildingOrders = paidOrders.filter((o) => o.couponUnitLabel || o.couponCode);
  const perUnitMap = new Map<string, BuildingUnitPerUnitRow>();
  buildingOrders.forEach((order) => {
    if (!order.couponUnitLabel && !order.couponCode) return;
    const key = (order.couponUnitLabel ?? order.couponCode ?? '').trim().toLowerCase();
    if (!key) return;
    const current = perUnitMap.get(key) ?? {
      unitLabel: order.couponUnitLabel ?? order.couponCode ?? '—',
      buildingWing: order.couponBuildingWing ?? resolveBuildingWing({ unitLabel: order.couponUnitLabel }),
      couponCode: order.couponCode,
      redemptionCount: 0,
      totalDiscount: 0,
    };
    current.redemptionCount += 1;
    current.totalDiscount += order.couponDiscountAmount ?? 0;
    perUnitMap.set(key, current);
  });
  const buildingUnits: BuildingUnitDashboardStats | undefined =
    buildingCoupons.length > 0
      ? {
          hotelWingIssued: hotelWingCoupons.length,
          hotelWingEverUsed: hotelWingCoupons.filter((c) => (c.usedCount ?? 0) > 0).length,
          towerWingIssued: towerWingCoupons.length,
          towerWingEverUsed: towerWingCoupons.filter((c) => (c.usedCount ?? 0) > 0).length,
          neverUsed: buildingCoupons.filter((c) => (c.usedCount ?? 0) === 0).length,
          totalDiscountInRange: buildingOrders.reduce((s, o) => s + (o.couponDiscountAmount ?? 0), 0),
          perUnit: Array.from(perUnitMap.values()).sort((a, b) => b.totalDiscount - a.totalDiscount),
        }
      : undefined;
  const byStaff = new Map<number, { redemptions: number; discountAmount: number }>();
  paidOrders.forEach((order) => {
    if (!order.couponDiscountAmount || !order.couponAppliedByStaffId) return;
    const current = byStaff.get(order.couponAppliedByStaffId) ?? { redemptions: 0, discountAmount: 0 };
    current.redemptions += 1;
    current.discountAmount += order.couponDiscountAmount;
    byStaff.set(order.couponAppliedByStaffId, current);
  });

  return {
    campaigns: campaignsRows,
    totalRevenueFromCoupons,
    usageByStaff: Array.from(byStaff.entries()).map(([staffId, value]) => ({ staffId, ...value })),
    recentRedemptions: usageLogs
      .filter((log) => log.result === 'redeemed' || log.result === 'applied')
      .slice(0, 20)
      .map((log) => {
        const coupon = coupons.find((c) => c.code === log.couponCode);
        return {
          couponCode: log.couponCode,
          campaignId: log.campaignId,
          orderId: log.orderId,
          staffId: log.staffId,
          usedAt: log.attemptedAt,
          unitLabel: coupon?.unitLabel ?? undefined,
        };
      }),
    buildingUnits,
  };
}

// --- Executive Dashboard (Admin) - Real-time metrics (5-min delay max) ---

export interface ExecutiveDashboardMetrics {
  /** $/hour today vs same day last week */
  salesVelocityCurrent: number;
  salesVelocityLastWeek: number;
  salesVelocityChangePercent: number;
  /** Open table count and turn rate per hour */
  openTableCount: number;
  totalTables: number;
  turnRatePerHour: number;
  /** Labor cost ratio placeholder (updates with clock-in/out when labor data exists) */
  laborCostRatio: number | null;
  laborCostRatioLabel: string;
  /** Void/comp tracking: $ amount + employee ID */
  voidCompEntries: { type: 'void' | 'comp' | 'override'; orderId: string; amount: number; employeeId?: number; employeeName?: string; timestamp: number; reason?: string }[];
  voidCompTotalToday: number;
  /** 86'd items (automatic alert) */
  eightySixedItems: { id: number; name: string; reason: 'unavailable' | 'daily_limit' }[];
  /** Order SLA: delayed orders (not met SLA) + average times for performance monitoring */
  orderSLA: {
    /** Orders placed but not started in kitchen past SLA */
    delayedNotStarted: { orderId: string; orderLabel: string; placedAt: number; minutesWaiting: number; slaMinutes: number }[];
    /** Orders started in kitchen but preparing too long (past warning/danger) */
    delayedPreparing: { orderId: string; orderLabel: string; startedAt: number; minutesInPrep: number; slaWarning: number; slaDanger: number }[];
    /** Orders ready for pickup but not served past SLA */
    delayedReadyNotServed: { orderId: string; orderLabel: string; readyAt: number; minutesWaiting: number; slaMinutes: number }[];
    /** Average minutes: placed → kitchen start (for orders that have started) */
    averageMinutesToStart: number | null;
    /** Average minutes: kitchen start → ready (prep time) */
    averagePrepMinutes: number | null;
    /** Average minutes: ready → served */
    averageServeMinutes: number | null;
    /** Average minutes: placed → served (total) */
    averageTotalMinutes: number | null;
    sampleCountToStart: number;
    sampleCountPrep: number;
    sampleCountServe: number;
    sampleCountTotal: number;
  };
}

function orderLabel(order: Order): string {
  if (order.orderType === 'takeaway') return `TAKEOUT-${order.takeawayNumber ?? order.id}`;
  if (order.orderType === 'online') return 'ONLINE';
  if (order.orderType === 'buffet') return `Buffet ${order.tableName ?? order.tableId ?? ''}`;
  return order.tableName ?? `Table ${order.tableId ?? order.tableNumber ?? ''}`;
}

export function getExecutiveDashboardMetrics(
  orders: Order[],
  tables: Table[],
  _registerSessions: RegisterSession[],
  menuItems: MenuItem[],
  sameDayLastWeekOrders: Order[],
  staffMap?: Map<number, string>,
  slaSettings?: KitchenSLASettings | null
): ExecutiveDashboardMetrics {
  const now = Date.now();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayStartMs = todayStart.getTime();

  const todaysOrders = orders.filter((o) => o.timestamp >= todayStartMs && o.status !== 'cancelled' && o.status !== 'refunded');
  const paidToday = todaysOrders.filter((o) => o.paymentStatus === 'paid');
  const salesToday = paidToday.reduce((sum, o) => sum + (o.totalAmount ?? 0), 0);
  const hoursElapsed = Math.max(0.25, (now - todayStartMs) / (1000 * 60 * 60));
  const salesVelocityCurrent = salesToday / hoursElapsed;

  const lastWeekStart = todayStartMs - 7 * 24 * 60 * 60 * 1000;
  const lastWeekEnd = lastWeekStart + 24 * 60 * 60 * 1000 - 1;
  const sameDayLastWeek = sameDayLastWeekOrders.filter(
    (o) => o.timestamp >= lastWeekStart && o.timestamp <= lastWeekEnd && o.paymentStatus === 'paid' && o.status !== 'cancelled' && o.status !== 'refunded'
  );
  const salesLastWeekSameDay = sameDayLastWeek.reduce((sum, o) => sum + (o.totalAmount ?? 0), 0);
  const salesVelocityLastWeek = salesLastWeekSameDay / 24;
  const salesVelocityChangePercent = salesVelocityLastWeek ? ((salesVelocityCurrent - salesVelocityLastWeek) / salesVelocityLastWeek) * 100 : 0;

  const occupiedTables = tables.filter((t) => t.status === 'occupied').length;
  const openTableCount = occupiedTables;
  const totalTables = tables.length;
  const dineInPaidToday = paidToday.filter((o) => o.orderType === 'dine-in' && o.tableId != null);
  const uniqueTablesTurned = new Set(dineInPaidToday.map((o) => o.tableId)).size;
  const turnRatePerHour = totalTables ? (uniqueTablesTurned / totalTables) / hoursElapsed : 0;

  const voidCompEntries: ExecutiveDashboardMetrics['voidCompEntries'] = [];
  todaysOrders.forEach((order) => {
    const amount = order.totalAmount ?? order.items.reduce((s, i) => s + i.price * i.quantity, 0);
    if (order.isComped || order.status === 'comped') {
      voidCompEntries.push({
        type: 'comp',
        orderId: order.id,
        amount,
        employeeId: order.overriddenBy,
        employeeName: order.overriddenBy != null ? staffMap?.get(order.overriddenBy) : undefined,
        timestamp: order.overrideTimestamp ?? order.timestamp,
        reason: order.overrideReason,
      });
    }
    if (order.status === 'cancelled' || order.status === 'refunded') {
      voidCompEntries.push({
        type: 'void',
        orderId: order.id,
        amount,
        employeeId: order.overriddenBy,
        employeeName: order.overriddenBy != null ? staffMap?.get(order.overriddenBy) : undefined,
        timestamp: order.overrideTimestamp ?? order.timestamp,
        reason: order.overrideReason,
      });
    }
    if (order.discountApprovedBy != null && (order.manualDiscount ?? 0) > 0) {
      voidCompEntries.push({
        type: 'override',
        orderId: order.id,
        amount: order.manualDiscount ?? 0,
        employeeId: order.discountApprovedBy,
        employeeName: staffMap?.get(order.discountApprovedBy),
        timestamp: order.discountApprovalTimestamp ?? order.timestamp,
        reason: order.discountApprovalReason,
      });
    }
  });
  voidCompEntries.sort((a, b) => b.timestamp - a.timestamp);
  const voidCompTotalToday = voidCompEntries.reduce((s, e) => s + e.amount, 0);

  const eightySixedItems: ExecutiveDashboardMetrics['eightySixedItems'] = [];
  menuItems.forEach((item) => {
    if (item.isAvailable === false) {
      eightySixedItems.push({ id: item.id, name: item.name, reason: 'unavailable' });
    } else if (item.hasDailyAvailability && (item.dailyAvailability ?? 0) <= 0 && (item.dailyAvailabilityLimit ?? 0) > 0) {
      eightySixedItems.push({ id: item.id, name: item.name, reason: 'daily_limit' });
    }
  });

  const minutesToStart = slaSettings?.minutesToStart ?? 5;
  const minutesToServe = slaSettings?.minutesToServe ?? 5;
  const warningMinutes = slaSettings?.warningMinutes ?? 5;
  const dangerMinutes = slaSettings?.dangerMinutes ?? 10;

  const activeOrders = todaysOrders.filter(
    (o) => o.status === 'new' || o.status === 'preparing' || o.status === 'ready'
  );
  const delayedNotStarted: ExecutiveDashboardMetrics['orderSLA']['delayedNotStarted'] = [];
  const delayedPreparing: ExecutiveDashboardMetrics['orderSLA']['delayedPreparing'] = [];
  const delayedReadyNotServed: ExecutiveDashboardMetrics['orderSLA']['delayedReadyNotServed'] = [];

  activeOrders.forEach((order) => {
    const minsSincePlaced = (now - order.timestamp) / (60 * 1000);
    if (order.status === 'new' && minsSincePlaced > minutesToStart) {
      delayedNotStarted.push({
        orderId: order.id,
        orderLabel: orderLabel(order),
        placedAt: order.timestamp,
        minutesWaiting: Math.round(minsSincePlaced * 10) / 10,
        slaMinutes: minutesToStart,
      });
    }
    if (order.status === 'preparing' && order.preparingStartedAt != null) {
      const minsInPrep = (now - order.preparingStartedAt) / (60 * 1000);
      if (minsInPrep >= warningMinutes) {
        delayedPreparing.push({
          orderId: order.id,
          orderLabel: orderLabel(order),
          startedAt: order.preparingStartedAt,
          minutesInPrep: Math.round(minsInPrep * 10) / 10,
          slaWarning: warningMinutes,
          slaDanger: dangerMinutes,
        });
      }
    }
    if (order.status === 'ready' && order.readyAt != null) {
      const minsWaiting = (now - order.readyAt) / (60 * 1000);
      if (minsWaiting > minutesToServe) {
        delayedReadyNotServed.push({
          orderId: order.id,
          orderLabel: orderLabel(order),
          readyAt: order.readyAt,
          minutesWaiting: Math.round(minsWaiting * 10) / 10,
          slaMinutes: minutesToServe,
        });
      }
    }
  });

  const toStartMinutes: number[] = [];
  const prepMinutes: number[] = [];
  const serveMinutes: number[] = [];
  const totalMinutes: number[] = [];
  todaysOrders.forEach((order) => {
    if (order.preparingStartedAt != null && order.preparingStartedAt >= todayStartMs) {
      toStartMinutes.push((order.preparingStartedAt - order.timestamp) / (60 * 1000));
    }
    if (order.readyAt != null && order.preparingStartedAt != null && order.readyAt >= todayStartMs) {
      prepMinutes.push((order.readyAt - order.preparingStartedAt) / (60 * 1000));
    }
    if (order.servedAt != null && order.readyAt != null && order.servedAt >= todayStartMs) {
      serveMinutes.push((order.servedAt - order.readyAt) / (60 * 1000));
      totalMinutes.push((order.servedAt - order.timestamp) / (60 * 1000));
    }
  });
  const avg = (arr: number[]) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null);

  return {
    salesVelocityCurrent,
    salesVelocityLastWeek,
    salesVelocityChangePercent,
    openTableCount,
    totalTables,
    turnRatePerHour,
    laborCostRatio: null,
    laborCostRatioLabel: 'Connect labor/scheduling for live ratio',
    voidCompEntries,
    voidCompTotalToday,
    eightySixedItems,
    orderSLA: {
      delayedNotStarted,
      delayedPreparing,
      delayedReadyNotServed,
      averageMinutesToStart: avg(toStartMinutes),
      averagePrepMinutes: avg(prepMinutes),
      averageServeMinutes: avg(serveMinutes),
      averageTotalMinutes: avg(totalMinutes),
      sampleCountToStart: toStartMinutes.length,
      sampleCountPrep: prepMinutes.length,
      sampleCountServe: serveMinutes.length,
      sampleCountTotal: totalMinutes.length,
    },
  };
}

// --- Operational Reports (Daily) ---

/** Daily Sales Summary: gross, net, tax, discounts, payment breakdown. Recipient: All Directors. Timing: Daily 11:59 PM */
export interface DailySalesSummaryReport {
  date: string;
  grossSales: number;
  netSales: number;
  taxAmount: number;
  discountsApplied: number;
  paymentBreakdown: { method: string; amount: number; percentage: number }[];
  recipient: string;
  timing: string;
}

/** Labor Actual vs Scheduled. Recipient: Managing Partner + CFO. Timing: Real-time + daily summary */
export interface LaborActualVsScheduledReport {
  date: string;
  actualHours: number;
  scheduledHours: number;
  overtimeAlerts: { employeeId?: number; employeeName?: string; hours: number }[];
  laborPercentOfSales: number;
  recipient: string;
  timing: string;
  dataAvailable: boolean;
}

/** Roster shift shape for timesheet/labor report (subset of Shift) */
export interface RosterShiftForReport {
  staffId: number | null;
  start: string;
  end: string;
  actualHours?: number;
  date?: number;
  mealBreakStart?: string;
  unpaidMealMinutes?: number;
}

/** Manual timesheet entry shape for report */
export interface TimesheetEntryForReport {
  staffId: number;
  startTime: string;
  endTime: string;
  actualHours?: number;
  date: number;
}

export interface TimesheetLaborSummary {
  totalHours: number;
  totalScheduledHours: number;
  byStaff: Array<{ staffId: number; staffName: string; hours: number; scheduledHours: number }>;
}

export function scheduledHoursFromShift(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}

/** Build labor summary from roster shifts and manual timesheet entries (for a period). Used for Flash PnL and staff expense. */
export function getTimesheetLaborSummary(
  rosterShifts: RosterShiftForReport[],
  manualEntries: TimesheetEntryForReport[],
  staffMap: Map<number, string>
): TimesheetLaborSummary {
  const byStaff = new Map<number, { scheduled: number; actual: number }>();
  let totalScheduled = 0;
  let totalActual = 0;
  rosterShifts.forEach((s) => {
    const scheduled = paidHoursRounded({
      start: s.start,
      end: s.end,
      mealBreakStart: s.mealBreakStart,
      unpaidMealMinutes: s.unpaidMealMinutes,
    });
    const actual = s.actualHours ?? scheduled;
    if (s.staffId != null) {
      const cur = byStaff.get(s.staffId) ?? { scheduled: 0, actual: 0 };
      cur.scheduled += scheduled;
      cur.actual += actual;
      byStaff.set(s.staffId, cur);
      totalScheduled += scheduled;
      totalActual += actual;
    }
  });
  manualEntries.forEach((e) => {
    const hours = e.actualHours ?? scheduledHoursFromShift(e.startTime, e.endTime);
    const cur = byStaff.get(e.staffId) ?? { scheduled: 0, actual: 0 };
    cur.actual += hours;
    byStaff.set(e.staffId, cur);
    totalActual += hours;
  });
  const byStaffList = Array.from(byStaff.entries()).map(([staffId, v]) => ({
    staffId,
    staffName: staffMap.get(staffId) ?? String(staffId),
    hours: v.actual,
    scheduledHours: v.scheduled,
  }));
  return { totalHours: totalActual, totalScheduledHours: totalScheduled, byStaff: byStaffList };
}

/** Build Labor Actual vs Scheduled report from pre-fetched roster and timesheet data for one day. */
export function getLaborActualVsScheduledFromData(
  date: string,
  rosterShiftsForDay: RosterShiftForReport[],
  manualEntriesForDay: TimesheetEntryForReport[],
  staffMap: Map<number, string>,
  revenueForDay: number,
  laborRatePerHour?: number
): LaborActualVsScheduledReport {
  const summary = getTimesheetLaborSummary(rosterShiftsForDay, manualEntriesForDay, staffMap);
  const overtimeThreshold = 8;
  const overtimeAlerts = summary.byStaff
    .filter((s) => s.hours > overtimeThreshold)
    .map((s) => ({ employeeId: s.staffId, employeeName: s.staffName, hours: s.hours - overtimeThreshold }));
  const laborCost = laborRatePerHour != null ? summary.totalHours * laborRatePerHour : 0;
  const laborPercentOfSales = revenueForDay > 0 && laborCost > 0 ? (laborCost / revenueForDay) * 100 : 0;
  return {
    date,
    actualHours: summary.totalHours,
    scheduledHours: summary.totalScheduledHours,
    overtimeAlerts,
    laborPercentOfSales,
    recipient: 'Managing Partner + CFO',
    timing: 'Real-time + daily summary',
    dataAvailable: summary.totalHours > 0 || summary.totalScheduledHours > 0,
  };
}

/** Product Mix (PMIX): units per item, contribution margin by category, modifier analysis */
export interface PMIXReportRow {
  itemId: number;
  itemName: string;
  categoryName: string;
  unitsSold: number;
  grossSales: number;
  /** Total theoretical COGS for this item over the period, based on recipes (undefined when no recipe/cost). */
  cogs?: number;
  /** Allocated labor cost for this item over the period (based on share of gross sales). */
  laborCost?: number;
  contributionMargin: number;
  contributionMarginPercent: number;
}
export interface PMIXReport {
  date: string;
  rows: PMIXReportRow[];
  modifierAnalysis: { modifierOrNote: string; usageCount: number; ordersAffected: number }[];
  recipient: string;
  timing: string;
}

/** Void/Discount Audit: comps, voids, manager overrides with employee ID + time. Real-time alerts for >$25 */
export interface VoidDiscountAuditRow {
  timestamp: number;
  type: 'comp' | 'void' | 'manager_override';
  orderId: string;
  amount: number;
  employeeId?: number;
  employeeName?: string;
  reason?: string;
}
export interface VoidDiscountAuditReport {
  startDate: string;
  endDate: string;
  rows: VoidDiscountAuditRow[];
  highValueAlerts: VoidDiscountAuditRow[]; // comps/voids/overrides > $25
  recipient: string;
  timing: string;
}

export async function getDailySalesSummaryReport(date: string): Promise<DailySalesSummaryReport> {
  const rows = await getDailySales(date, date);
  const insights = await getSalesInsights(date, date);
  const row = rows[0];
  if (!row) {
    return {
      date,
      grossSales: 0,
      netSales: 0,
      taxAmount: 0,
      discountsApplied: 0,
      paymentBreakdown: (insights.payments || []).map((p) => ({ method: p.method, amount: p.amount, percentage: p.percentage })),
      recipient: 'All Directors',
      timing: 'Daily at 11:59 PM',
    };
  }
  return {
    date,
    grossSales: row.grossSales,
    netSales: row.netSales,
    taxAmount: row.taxCollected,
    discountsApplied: row.discounts,
    paymentBreakdown: insights.payments.map((p) => ({ method: p.method, amount: p.amount, percentage: p.percentage })),
    recipient: 'All Directors',
    timing: 'Daily at 11:59 PM',
  };
}

export function getLaborActualVsScheduledReport(_date: string): LaborActualVsScheduledReport {
  return {
    date: _date,
    actualHours: 0,
    scheduledHours: 0,
    overtimeAlerts: [],
    laborPercentOfSales: 0,
    recipient: 'Managing Partner + CFO',
    timing: 'Real-time + daily summary',
    dataAvailable: false,
  };
}

export async function getPMIXReport(
  startDate: string,
  endDate: string,
  recipes: Recipe[],
  inventoryItems: InventoryItem[],
  laborRatePerHour?: number
): Promise<PMIXReport> {
  const { start, end } = getDateRange(startDate, endDate);
  const [orders, categories] = await Promise.all([
    ordersService.getByDateRange(start, end),
    categoriesService.getAll(),
  ]);
  const paid = orders.filter((o) => o.paymentStatus === 'paid' && o.status !== 'cancelled' && o.status !== 'refunded');
  const categoryMap = buildCategoryMap(categories);

  // Precompute theoretical cost per serve for each recipe (inventory-connected or cost-only)
  const invCostMap = new Map<number, number>();
  inventoryItems.forEach((inv) => {
    invCostMap.set(inv.id, inv.costPerUnit ?? 0);
  });
  const recipeCostPerServe = new Map<number, number | null>();
  recipes.forEach((recipe) => {
    let total = 0;
    let ok = true;
    recipe.ingredients.forEach((ing) => {
      if (ing.inventoryItemId != null) {
        const costPerUnit = invCostMap.get(ing.inventoryItemId);
        if (costPerUnit == null) {
          ok = false;
          return;
        }
        total += ing.quantity * costPerUnit;
      } else if (ing.costPerUnit != null) {
        total += ing.quantity * ing.costPerUnit;
      } else {
        ok = false;
      }
    });
    recipeCostPerServe.set(recipe.menuItemId, ok ? total : null);
  });

  const itemMap = new Map<number, { name: string; categoryName: string; units: number; gross: number; cost: number }>();
  paid.forEach((order) => {
    order.items.forEach((item: OrderItem) => {
      const catKey = item.categoryId != null && item.categoryId !== '' ? String(item.categoryId) : 'uncategorised';
      const catName = categoryMap.get(catKey) || 'Uncategorised';
      const existing = itemMap.get(item.id);
      const qty = item.quantity ?? 0;
      const gross = item.price * qty;
      const costPerServe = recipeCostPerServe.get(item.id) ?? null;
      const lineCost = costPerServe != null ? costPerServe * qty : 0;
      if (!existing) {
        itemMap.set(item.id, { name: item.name, categoryName: catName, units: qty, gross, cost: lineCost });
      } else {
        existing.units += qty;
        existing.gross += gross;
        existing.cost += lineCost;
      }
    });
  });

  // Compute total labor cost for the period from rostered/actual hours × laborRatePerHour
  let totalLaborCost: number | undefined;
  if (laborRatePerHour != null && laborRatePerHour > 0) {
    try {
      // Collect roster shifts for all weeks overlapping the range
      const rosterShifts: RosterShiftForReport[] = [];
      const msPerDay = 24 * 60 * 60 * 1000;
      // Start from Monday of the start date
      const startDateStr = startDate;
      let currentWeekStart = getWeekStartTimestamp(startDateStr);
      while (currentWeekStart <= end) {
        const roster = await rosterService.getRosterByWeek(currentWeekStart);
        const shifts = roster?.shifts ?? [];
        shifts.forEach((s) => {
          if (s.date >= start && s.date <= end && s.staffId != null) {
            rosterShifts.push({
              staffId: s.staffId,
              start: s.start,
              end: s.end,
              actualHours: s.actualHours,
              date: s.date,
              mealBreakStart: s.mealBreakStart,
              unpaidMealMinutes: s.unpaidMealMinutes,
            });
          }
        });
        currentWeekStart += 7 * msPerDay;
      }

      // Manual / ad-hoc timesheet entries for the period
      const manualEntries = await timesheetService.getByDateRange(start, end);
      const manualForReport: TimesheetEntryForReport[] = manualEntries.map((e) => ({
        staffId: e.staffId,
        startTime: e.startTime,
        endTime: e.endTime,
        actualHours: e.actualHours,
        date: e.date,
      }));

      // We only need hours, not staff names, so pass an empty map
      const summary = getTimesheetLaborSummary(rosterShifts, manualForReport, new Map());
      if (summary.totalHours > 0) {
        totalLaborCost = summary.totalHours * laborRatePerHour;
      }
    } catch (e) {
      console.error('Failed to compute labor cost for PMIX:', e);
      totalLaborCost = undefined;
    }
  }

  const totalGross = Array.from(itemMap.values()).reduce((s, d) => s + d.gross, 0);

  const rows: PMIXReportRow[] = Array.from(itemMap.entries()).map(([itemId, data]) => ({
    itemId,
    itemName: data.name,
    categoryName: data.categoryName,
    unitsSold: data.units,
    grossSales: data.gross,
    cogs: data.cost > 0 ? data.cost : undefined,
    laborCost:
      totalLaborCost != null && totalGross > 0
        ? (totalLaborCost * (data.gross / totalGross))
        : undefined,
    contributionMargin: (() => {
      if (data.cost <= 0) return 0;
      const labor = totalLaborCost != null && totalGross > 0 ? (totalLaborCost * (data.gross / totalGross)) : 0;
      return data.gross - data.cost - labor;
    })(),
    contributionMarginPercent: (() => {
      if (data.cost <= 0 || data.gross <= 0) return 0;
      const labor = totalLaborCost != null && totalGross > 0 ? (totalLaborCost * (data.gross / totalGross)) : 0;
      return ((data.gross - data.cost - labor) / data.gross) * 100;
    })(),
  })).sort((a, b) => b.grossSales - a.grossSales);

  const modifierAnalysis: PMIXReport['modifierAnalysis'] = [];
  const noteCount = new Map<string, { count: number; orders: Set<string> }>();
  paid.forEach((order) => {
    order.items.forEach((item) => {
      const note = (item.notes && item.notes.trim()) ? item.notes.trim() : null;
      if (note) {
        const key = note.slice(0, 80);
        if (!noteCount.has(key)) noteCount.set(key, { count: 0, orders: new Set() });
        const entry = noteCount.get(key)!;
        entry.count += item.quantity;
        entry.orders.add(order.id);
      }
    });
  });
  noteCount.forEach((value, key) => {
    modifierAnalysis.push({ modifierOrNote: key, usageCount: value.count, ordersAffected: value.orders.size });
  });
  modifierAnalysis.sort((a, b) => b.usageCount - a.usageCount);

  return {
    date: endDate,
    rows,
    modifierAnalysis,
    recipient: 'Managing Partner',
    timing: 'Daily at 6 AM',
  };
}

export async function getVoidDiscountAuditReport(startDate: string, endDate: string, staffMap?: Map<number, string>): Promise<VoidDiscountAuditReport> {
  const { start, end } = getDateRange(startDate, endDate);
  const orders = await ordersService.getByDateRange(start, end);
  const rows: VoidDiscountAuditRow[] = [];
  orders.forEach((order) => {
    const amount = order.totalAmount ?? order.items.reduce((s, i) => s + i.price * i.quantity, 0);
    if (order.isComped || order.status === 'comped') {
      rows.push({
        timestamp: order.overrideTimestamp ?? order.timestamp,
        type: 'comp',
        orderId: order.id,
        amount,
        employeeId: order.overriddenBy,
        employeeName: order.overriddenBy != null ? staffMap?.get(order.overriddenBy) : undefined,
        reason: order.overrideReason,
      });
    }
    if (order.status === 'cancelled' || order.status === 'refunded') {
      rows.push({
        timestamp: order.overrideTimestamp ?? order.timestamp,
        type: 'void',
        orderId: order.id,
        amount,
        employeeId: order.overriddenBy,
        employeeName: order.overriddenBy != null ? staffMap?.get(order.overriddenBy) : undefined,
        reason: order.overrideReason,
      });
    }
    if ((order.discountApprovedBy != null || (order.manualDiscount ?? 0) > 0) && (order.manualDiscount ?? 0) > 0) {
      rows.push({
        timestamp: order.discountApprovalTimestamp ?? order.timestamp,
        type: 'manager_override',
        orderId: order.id,
        amount: order.manualDiscount ?? 0,
        employeeId: order.discountApprovedBy,
        employeeName: order.discountApprovedBy != null ? staffMap?.get(order.discountApprovedBy) : undefined,
        reason: order.discountApprovalReason,
      });
    }
  });
  rows.sort((a, b) => b.timestamp - a.timestamp);
  const highValueAlerts = rows.filter((r) => r.amount > 25);
  return {
    startDate,
    endDate,
    rows,
    highValueAlerts,
    recipient: 'All Directors',
    timing: 'Real-time alerts for comps/voids/overrides > $25',
  };
}

// --- Financial Reports (Weekly/Monthly): Theoretical vs Actual Food Cost ---

export interface FoodCostCategoryRow {
  category: string; // e.g. proteins, seafood, liquor
  theoreticalCOGS: number;
  actualCOGS: number;
  variance: number;
  variancePercent: number;
  lossPoint: boolean; // >2% variance
}

export interface FoodCostMenuItemRow {
  menuItemId: number;
  menuItemName: string;
  quantitySold: number;
  theoreticalCOGS: number;
}

export interface TheoreticalVsActualFoodCostReport {
  startDate: string;
  endDate: string;
  theoreticalCOGSTotal: number;
  actualCOGSTotal: number;
  varianceTotal: number;
  variancePercentTotal: number;
  byCategory: FoodCostCategoryRow[];
  byMenuItem: FoodCostMenuItemRow[];
  lossPoints: FoodCostCategoryRow[];
}

function getInventoryCost(inv: InventoryItem): number {
  const stock = inv.isBulkPurchase && inv.bulkPurchases
    ? inv.bulkPurchases.reduce((s, b) => s + (b.remainingQuantity ?? 0), 0)
    : inv.stock;
  const cost = inv.costPerUnit ?? 0;
  return stock * cost;
}

export function getTheoreticalVsActualFoodCostReport(
  startDate: string,
  endDate: string,
  orders: Order[],
  recipes: Recipe[],
  inventoryItems: InventoryItem[],
  menuItems: MenuItem[] = []
): TheoreticalVsActualFoodCostReport {
  const { start, end } = getDateRange(startDate, endDate);
  const paid = orders.filter((o) => o.timestamp >= start && o.timestamp <= end && o.paymentStatus === 'paid' && o.status !== 'cancelled' && o.status !== 'refunded');

  const invCostMap = new Map<number, number>();
  inventoryItems.forEach((inv) => {
    invCostMap.set(inv.id, inv.costPerUnit ?? 0);
  });

  const categoryToInv = new Map<string, number[]>();
  inventoryItems.forEach((inv) => {
    const cat = inv.category || 'Other';
    if (!categoryToInv.has(cat)) categoryToInv.set(cat, []);
    categoryToInv.get(cat)!.push(inv.id);
  });

  const theoreticalByCategory = new Map<string, number>();
  const usageByInvId = new Map<number, number>();
  const byMenuItemMap = new Map<number, { quantitySold: number; theoreticalCOGS: number }>();

  paid.forEach((order) => {
    order.items.forEach((item) => {
      const recipe = recipes.find((r) => r.menuItemId === item.id);
      if (!recipe) return;
      const qty = item.quantity ?? 0;
      let itemCOGS = 0;
      recipe.ingredients.forEach((ing) => {
        if (ing.inventoryItemId != null) {
          const costPerUnit = invCostMap.get(ing.inventoryItemId) ?? 0;
          const cost = ing.quantity * qty * costPerUnit;
          itemCOGS += cost;
          usageByInvId.set(ing.inventoryItemId, (usageByInvId.get(ing.inventoryItemId) ?? 0) + ing.quantity * qty);
          const inv = inventoryItems.find((i) => i.id === ing.inventoryItemId);
          const cat = inv?.category || 'Other';
          theoreticalByCategory.set(cat, (theoreticalByCategory.get(cat) ?? 0) + cost);
        } else if (ing.costPerUnit != null) {
          const cost = ing.quantity * qty * ing.costPerUnit;
          itemCOGS += cost;
          theoreticalByCategory.set('Cost-only', (theoreticalByCategory.get('Cost-only') ?? 0) + cost);
        }
      });
      const existing = byMenuItemMap.get(item.id) ?? { quantitySold: 0, theoreticalCOGS: 0 };
      byMenuItemMap.set(item.id, {
        quantitySold: existing.quantitySold + qty,
        theoreticalCOGS: existing.theoreticalCOGS + itemCOGS,
      });
    });
  });

  const theoreticalCOGSTotal = Array.from(theoreticalByCategory.values()).reduce((s, v) => s + v, 0);
  // Actual COGS: from inventory depletion for inventory-linked categories; for "Cost-only" (recipe-defined cost, no depletion) use theoretical as proxy.
  const byCategory: FoodCostCategoryRow[] = [];
  theoreticalByCategory.forEach((theoreticalCOGS, category) => {
    const invIds = categoryToInv.get(category) ?? [];
    const actualCOGS = category === 'Cost-only'
      ? theoreticalCOGS
      : invIds.reduce((sum, id) => {
          const inv = inventoryItems.find((i) => i.id === id);
          if (!inv) return sum;
          const used = usageByInvId.get(id) ?? 0;
          const costPerUnit = inv.costPerUnit ?? 0;
          return sum + used * costPerUnit;
        }, 0);
    const variance = actualCOGS - theoreticalCOGS;
    const variancePercent = theoreticalCOGS ? (variance / theoreticalCOGS) * 100 : 0;
    const lossPoint = Math.abs(variancePercent) > 2;
    byCategory.push({
      category,
      theoreticalCOGS,
      actualCOGS,
      variance,
      variancePercent,
      lossPoint,
    });
  });

  const actualCOGSTotal = byCategory.reduce((s, r) => s + r.actualCOGS, 0);
  const varianceTotal = actualCOGSTotal - theoreticalCOGSTotal;
  const variancePercentTotal = theoreticalCOGSTotal ? (varianceTotal / theoreticalCOGSTotal) * 100 : 0;
  const lossPoints = byCategory.filter((r) => r.lossPoint);

  const menuItemIdToName = new Map<number, string>();
  menuItems.forEach((m) => menuItemIdToName.set(m.id, m.name));
  const byMenuItem: FoodCostMenuItemRow[] = Array.from(byMenuItemMap.entries())
    .map(([menuItemId, data]) => ({
      menuItemId,
      menuItemName: menuItemIdToName.get(menuItemId) ?? `Item ${menuItemId}`,
      quantitySold: data.quantitySold,
      theoreticalCOGS: data.theoreticalCOGS,
    }))
    .sort((a, b) => b.theoreticalCOGS - a.theoreticalCOGS);

  return {
    startDate,
    endDate,
    theoreticalCOGSTotal,
    actualCOGSTotal,
    varianceTotal,
    variancePercentTotal,
    byCategory,
    byMenuItem,
    lossPoints,
  };
}

// --- Flash P&L (Investor Format) ---

export interface FlashPnlSettings {
  weeklyRevenueBudget?: number;
  cogsTargetPercent?: number;
  laborTargetPercent?: number;
  laborCost?: number;
  /** When using timesheet-derived labor, cost = totalHours * laborRatePerHour */
  laborRatePerHour?: number;
  overtimeCost?: number;
  fixedCostsRent?: number;
  fixedCostsInsurance?: number;
  fixedCostsSalaries?: number;
  cashPosition?: number;
  beverageCategoryNames?: string[];
}

export interface FlashPnlReport {
  weekEnding: string;
  revenue: number;
  budget: number | null;
  variancePercent: number | null;
  foodSales: number;
  beverageSales: number;
  cogs: number;
  cogsPercent: number;
  cogsTargetPercent: number | null;
  cogsVarianceImpact: number | null;
  laborCost: number;
  laborPercent: number;
  laborTargetPercent: number | null;
  overtimeCost: number;
  contributionMargin: number;
  fixedCostsRent: number;
  fixedCostsInsurance: number;
  fixedCostsSalaries: number;
  fixedCostsTotal: number;
  netOperatingIncome: number;
  cashPosition: number | null;
}

/** Get week-ending date (Saturday) for a given date string or today, in local timezone */
export function getWeekEndingDate(dateStr?: string): string {
  const d = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
  const day = d.getDay();
  const satOffset = day === 6 ? 0 : day === 0 ? -1 : 6 - day;
  d.setDate(d.getDate() + satOffset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayNum}`;
}

/** Get week-start (Monday 00:00) timestamp for a given date string or today */
export function getWeekStartTimestamp(dateStr?: string): number {
  const d = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function getFlashPnlReport(
  weekEnding: string,
  orders: Order[],
  recipes: Recipe[],
  inventoryItems: InventoryItem[],
  categories: Category[],
  settings?: FlashPnlSettings | null,
  timesheetLaborSummary?: TimesheetLaborSummary | null
): FlashPnlReport {
  const start = new Date(weekEnding + 'T00:00:00').getTime() - 6 * 24 * 60 * 60 * 1000;
  const end = new Date(weekEnding + 'T23:59:59').getTime();
  const weekOrders = orders.filter(
    (o) => o.timestamp >= start && o.timestamp <= end && o.paymentStatus === 'paid' && o.status !== 'cancelled' && o.status !== 'refunded'
  );
  const revenue = weekOrders.reduce((s, o) => s + (o.totalAmount ?? 0), 0);

  const beverageNames = new Set((settings?.beverageCategoryNames ?? ['Beverages', 'Drinks', 'Coffee', 'Bar']).map((n) => n.toLowerCase()));
  const categoryIdToName = new Map(categories.map((c) => [String(c.id), c.name]));
  let foodSales = 0;
  let beverageSales = 0;
  weekOrders.forEach((order) => {
    order.items.forEach((item: OrderItem) => {
      const catName = (categoryIdToName.get(String(item.categoryId)) ?? '').toLowerCase();
      const isBeverage = Array.from(beverageNames).some((b) => catName.includes(b) || b.includes(catName));
      const amount = item.price * (item.quantity ?? 0);
      if (isBeverage) beverageSales += amount;
      else foodSales += amount;
    });
  });

  const invCostMap = new Map<number, number>();
  inventoryItems.forEach((inv) => invCostMap.set(inv.id, inv.costPerUnit ?? 0));
  let cogs = 0;
  weekOrders.forEach((order) => {
    order.items.forEach((item) => {
      const recipe = recipes.find((r) => r.menuItemId === item.id);
      if (!recipe) return;
      const qty = item.quantity ?? 0;
      recipe.ingredients.forEach((ing) => {
        const costPerUnit = ing.inventoryItemId != null
          ? (invCostMap.get(ing.inventoryItemId) ?? 0)
          : (ing.costPerUnit ?? 0);
        cogs += ing.quantity * qty * costPerUnit;
      });
    });
  });
  const cogsPercent = revenue ? (cogs / revenue) * 100 : 0;
  const cogsTarget = settings?.cogsTargetPercent ?? null;
  const cogsVarianceImpact = cogsTarget != null && revenue ? (cogsPercent - cogsTarget) / 100 * revenue : null;

  const laborRate = settings?.laborRatePerHour;
  const laborCostFromTimesheet = timesheetLaborSummary && laborRate != null ? timesheetLaborSummary.totalHours * laborRate : null;
  const laborCost = laborCostFromTimesheet ?? settings?.laborCost ?? 0;
  const laborPercent = revenue ? (laborCost / revenue) * 100 : 0;
  const laborTargetPercent = settings?.laborTargetPercent ?? null;
  const overtimeCost = settings?.overtimeCost ?? 0;

  const contributionMargin = revenue - cogs - laborCost;

  const fixedCostsRent = settings?.fixedCostsRent ?? 0;
  const fixedCostsInsurance = settings?.fixedCostsInsurance ?? 0;
  const fixedCostsSalaries = settings?.fixedCostsSalaries ?? 0;
  const fixedCostsTotal = fixedCostsRent + fixedCostsInsurance + fixedCostsSalaries;
  const netOperatingIncome = contributionMargin - fixedCostsTotal;
  const cashPosition = settings?.cashPosition ?? null;
  const budget = settings?.weeklyRevenueBudget ?? null;
  const variancePercent = budget != null && budget > 0 ? ((revenue - budget) / budget) * 100 : null;

  return {
    weekEnding,
    revenue,
    budget,
    variancePercent,
    foodSales,
    beverageSales,
    cogs,
    cogsPercent,
    cogsTargetPercent: cogsTarget,
    cogsVarianceImpact,
    laborCost,
    laborPercent,
    laborTargetPercent,
    overtimeCost,
    contributionMargin,
    fixedCostsRent,
    fixedCostsInsurance,
    fixedCostsSalaries,
    fixedCostsTotal,
    netOperatingIncome,
    cashPosition,
  };
}
